/**
 * The entries a booking option names: single resources, and whole sections.
 * The two lists form one list of alternatives. A booking takes one member.
 */
export interface ResourceSelection {
  scheduleResourceIds: number[];
  resourceCategoryIds: number[];
}

export const EMPTY_RESOURCE_SELECTION: ResourceSelection = {
  scheduleResourceIds: [],
  resourceCategoryIds: [],
};

// One select holds both types of entry, so each option value carries its type.
export const categoryValue = (id: number): string => `category:${id}`;
export const resourceValue = (id: number): string => `resource:${id}`;

/**
 * Splits the option values of the select into the two id lists of the
 * payload. A value with an unknown type or a bad id is dropped.
 */
export function splitOptionValues(values: string[]): ResourceSelection {
  const selection: ResourceSelection = {
    scheduleResourceIds: [],
    resourceCategoryIds: [],
  };

  values.forEach((value) => {
    const [type, rawId] = value.split(":");
    const id = Number(rawId);
    if (!rawId || !Number.isInteger(id)) {
      return;
    }
    if (type === "category") {
      selection.resourceCategoryIds.push(id);
    } else if (type === "resource") {
      selection.scheduleResourceIds.push(id);
    }
  });

  return selection;
}

/**
 * The plural of a section title for the "All" option: "Simulator Bay" gives
 * "Simulator Bays". A title that ends in "s" stays as it is.
 */
export function pluralSectionTitle(title: string): string {
  return /s$/i.test(title) ? title : `${title}s`;
}

/**
 * Whether the picker offers the "All" entry for a section. The picker offers
 * it only for a section with an active bookable resource. A section the
 * option already names stays offered, even with no active resource, so the
 * admin can still remove the chip.
 */
export function categoryOfferable(
  category: { id: number },
  resources: { resourceCategoryId: number; active: boolean }[],
  selectedCategoryIds: number[]
): boolean {
  if (selectedCategoryIds.includes(category.id)) {
    return true;
  }

  return resources.some(
    (resource) =>
      resource.resourceCategoryId === category.id && resource.active
  );
}

/**
 * The label of the "All" entry for a section: "All Simulator Bays" for the
 * section "Simulator Bay". Shared by the picker option and by the closed
 * block summary, so both give the same label for the same section.
 */
export function allEntryLabel(title: string, allLabel: string): string {
  return `${allLabel} ${pluralSectionTitle(title)}`;
}

/**
 * The entries of a booking option, for the one-line summary of a closed
 * Resources block (Figma 145:58049): each "All" entry first, then each
 * resource, in the order of the given lists.
 */
export function selectionSummary(
  selection: ResourceSelection,
  resources: { id: number; title: string }[],
  categories: { id: number; title: string }[],
  allLabel: string
): string[] {
  const categoryNames = categories
    .filter((category) => selection.resourceCategoryIds.includes(category.id))
    .map((category) => allEntryLabel(category.title, allLabel));
  const resourceNames = resources
    .filter((resource) => selection.scheduleResourceIds.includes(resource.id))
    .map((resource) => resource.title);

  return [...categoryNames, ...resourceNames];
}

/**
 * The number of resources that the entries of a booking option cover, for the
 * count in the select control (Figma 145:57563). A section covers each of its
 * active resources. A resource that the option names on its own counts too,
 * also when it is inactive. A resource that the option names twice (on its
 * own and through its section) counts once. An id that is not in the list
 * counts as nothing.
 */
export function coveredResourceCount(
  selection: ResourceSelection,
  resources: { id: number; resourceCategoryId: number; active: boolean }[]
): number {
  return resources.filter(
    (resource) =>
      selection.scheduleResourceIds.includes(resource.id) ||
      (resource.active &&
        selection.resourceCategoryIds.includes(resource.resourceCategoryId))
  ).length;
}

/**
 * The text the select control shows after a pick (Figma 145:57563): a count,
 * not the chips. The chips sit in a row under the control, not in the
 * control itself. Each label comes pre-translated, so this function only
 * picks the right one for the count.
 */
export function resourceCountLabel(
  count: number,
  noneLabel: string,
  oneLabel: string,
  manyLabel: string
): string {
  if (count === 0) {
    return noneLabel;
  }

  return count === 1 ? oneLabel : manyLabel;
}

/**
 * Removes each single resource whose section the option also names whole.
 * "All Simulators" already covers Test 1, so a chip for Test 1 next to it
 * names the bay twice (CFTB-3551). The section entry wins, so a pick of a
 * section takes the place of the single bays of that section.
 */
export function dropCoveredResources(
  selection: ResourceSelection,
  resources: { id: number; resourceCategoryId: number }[]
): ResourceSelection {
  const coveredIds = new Set(
    resources
      .filter((resource) =>
        selection.resourceCategoryIds.includes(resource.resourceCategoryId)
      )
      .map((resource) => resource.id)
  );

  return {
    scheduleResourceIds: selection.scheduleResourceIds.filter(
      (id) => !coveredIds.has(id)
    ),
    resourceCategoryIds: selection.resourceCategoryIds,
  };
}
